// apps/web/src/components/ideas/IdeaCard.tsx
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { MoreVertical, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { Idea } from "@/hooks/use-ideas";
import { PlatformIcon } from "@/components/social/PlatformIcon";

interface Props {
  idea: Idea;
  onEdit: () => void;
  onDelete: () => void;
  onPromote?: () => void;
  onClick: () => void;
  canEdit: boolean;
}

export function IdeaCard({ idea, onEdit, onDelete, onPromote, onClick, canEdit }: Props) {
  const { t } = useTranslation("ideas");
  return (
    <div
      onClick={onClick}
      className="cursor-pointer rounded-md border bg-card p-3 shadow-sm hover:shadow transition-shadow group"
    >
      <div className="flex items-start justify-between gap-2">
        <h4 className="text-sm font-semibold leading-tight">{idea.title}</h4>
        {canEdit && (
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <button
                className="text-muted-foreground hover:text-foreground opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={(e) => e.stopPropagation()}
                aria-label={t("ideas:card.actions")}
              >
                <MoreVertical className="h-4 w-4" />
              </button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content className="z-50 min-w-[140px] rounded-md border bg-popover p-1 shadow-md" align="end">
                <DropdownMenu.Item onSelect={onEdit} className="rounded px-2 py-1.5 text-sm hover:bg-accent cursor-pointer">{t("ideas:card.edit")}</DropdownMenu.Item>
                {onPromote && (
                  <DropdownMenu.Item onSelect={onPromote} className="rounded px-2 py-1.5 text-sm hover:bg-accent cursor-pointer">{t("ideas:card.promoteToProject")}</DropdownMenu.Item>
                )}
                <DropdownMenu.Item onSelect={onDelete} className="rounded px-2 py-1.5 text-sm text-destructive hover:bg-destructive/10 cursor-pointer">{t("common:actions.delete")}</DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        )}
      </div>
      {idea.description && (
        <p className="mt-1 text-xs text-muted-foreground line-clamp-2">{idea.description}</p>
      )}
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        {idea.platforms.map((p) => (
          <span key={p} title={p} className="inline-flex">
            <PlatformIcon platform={p} size={16} />
          </span>
        ))}
        {idea.formats.map((f) => (
          <span key={f} className="rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wide">{f}</span>
        ))}
        {idea.aiGenerated && (
          <span className="inline-flex items-center gap-1 rounded bg-violet-100 px-1.5 py-0.5 text-[10px] text-violet-700">
            <Sparkles className="h-3 w-3" /> AI
          </span>
        )}
      </div>
    </div>
  );
}
